import { couponUnlockCodeBulkUpload_couponUnlockCodeBulkUploadAndExtract_unlockCodes } from "@saleor/coupons/types/couponUnlockCodeBulkUpload";
import { useCallback, useReducer, useState } from "react";

import { generateUnlockCodeErrorDetail, hasError } from "./utils";

export interface IUnlockCode {
  componentKey: number;
  unlockCode: string;
  unlockCodeUsageLimit: number;
  couponReportTags: string[];
  description: string;
}

export interface IUnlockCodeErrorDetail {
  unlockCode?: string;
  couponReportTags?: string;
  description?: string;
}

export interface TUnlockCodeListState {
  componentKeyCounter: number;
  listArr: IUnlockCode[];
}

interface IAddUnlockCodeAction {
  type: "add-unlock-code";
}

interface IModifyUnlockCodeAction {
  type: "modify-unlock-code";
  modifiedComponentKey: number;
  modifiedUnlockCodeObj: IUnlockCode;
}

interface IDeleteUnlockCodeAction {
  type: "delete-unlock-code";
  deletedComponentKey: number;
}

interface IBulkAddUnlockCodesAction {
  type: "bulk-add-unlock-codes";
  unlockCodes: couponUnlockCodeBulkUpload_couponUnlockCodeBulkUploadAndExtract_unlockCodes[];
}

export type TUnlockCodeListReducer =
  | IAddUnlockCodeAction
  | IModifyUnlockCodeAction
  | IDeleteUnlockCodeAction
  | IBulkAddUnlockCodesAction;

const unlockCodesListStateReducer = (
  state: TUnlockCodeListState,
  action: TUnlockCodeListReducer
): TUnlockCodeListState => {
  switch (action.type) {
    case "add-unlock-code":
      return {
        componentKeyCounter: state.componentKeyCounter + 1,
        listArr: [
          ...state.listArr,
          {
            componentKey: state.componentKeyCounter,
            couponReportTags: [],
            description: "",
            unlockCode: "",
            unlockCodeUsageLimit: 0
          }
        ]
      };
    case "modify-unlock-code":
      return {
        componentKeyCounter: state.componentKeyCounter,
        listArr: state.listArr.map(unlockCodeObj => {
          if (action.modifiedComponentKey === unlockCodeObj.componentKey) {
            return action.modifiedUnlockCodeObj;
          }
          return unlockCodeObj;
        })
      };

    case "delete-unlock-code":
      return {
        componentKeyCounter: state.componentKeyCounter,
        listArr: state.listArr.filter(
          unlockCodeObj =>
            unlockCodeObj.componentKey !== action.deletedComponentKey
        )
      };
    case "bulk-add-unlock-codes":
      return {
        componentKeyCounter: action.unlockCodes.length + 1,
        listArr: action.unlockCodes.map((unlockCode, idx) => ({
          componentKey: state.componentKeyCounter + idx,
          couponReportTags: unlockCode.tags.map(tag => tag),
          description: unlockCode.description,
          unlockCode: unlockCode.unlockCode,
          unlockCodeUsageLimit: unlockCode.usageLimit
        }))
      };
  }
};

const generateInitialState = (): TUnlockCodeListState => ({
  componentKeyCounter: 1,
  listArr: [
    {
      componentKey: 0,
      couponReportTags: [],
      description: "",
      unlockCode: "",
      unlockCodeUsageLimit: 0
    }
  ]
});

const useUnlockCodesState = () => {
  const [unlockCodeState, unlockCodeStateDispatcher] = useReducer(
    unlockCodesListStateReducer,
    null,
    generateInitialState
  );
  const [unlockCodeErrorLookup, setUnlockCodeErrorLookup] = useState<
    Record<number, IUnlockCodeErrorDetail>
  >({});

  // This callback checks for FE Validation first before calling BE Mutation
  const onInsertCallback = useCallback(
    (unlockCodeCreateCallback: () => void) => {
      let hasValidationError = false;
      // Check for validation fields error (creates lookup table for errors on unlockCode object.)
      setUnlockCodeErrorLookup(
        unlockCodeState.listArr.reduce((errorLookup, currUnlockCodeObj) => {
          const unlockCodeErrorDetail = generateUnlockCodeErrorDetail(
            currUnlockCodeObj,
            unlockCodeState.listArr
          );
          if (hasError(unlockCodeErrorDetail)) {
            hasValidationError = true;
            return {
              ...errorLookup,
              [currUnlockCodeObj.componentKey]: unlockCodeErrorDetail
            };
          }
          return errorLookup;
        }, {})
      );
      if (!hasValidationError) {
        unlockCodeCreateCallback();
      }
    },
    [unlockCodeState]
  );

  const setExistingUnlockCodesError = useCallback(
    (existingUnlockCodesList: string[]) => {
      // Check for validation fields error (creates lookup table for errors on unlockCode object.)
      setUnlockCodeErrorLookup(
        unlockCodeState.listArr.reduce((errorLookup, currUnlockCodeObj) => {
          const unlockCodeErrorDetail = generateUnlockCodeErrorDetail(
            currUnlockCodeObj,
            unlockCodeState.listArr,
            existingUnlockCodesList
          );
          if (hasError(unlockCodeErrorDetail)) {
            return {
              ...errorLookup,
              [currUnlockCodeObj.componentKey]: unlockCodeErrorDetail
            };
          }
          return errorLookup;
        }, {})
      );
    },
    [unlockCodeState]
  );

  const getUnlockCodeObjErrorFn = useCallback(
    (componentKey: number): IUnlockCodeErrorDetail =>
      unlockCodeErrorLookup[componentKey],
    [unlockCodeErrorLookup]
  );

  return {
    unlockCodeState,
    unlockCodeStateDispatcher,
    onInsertCallback,
    getUnlockCodeObjErrorFn,
    setExistingUnlockCodesError
  };
};

export default useUnlockCodesState;
